import { useBPLogger } from '@/composables/business-process-logger';
import { useRouterHelpers } from '@/composables/router-helpers';
import { AccountModel, DraftInitInfoDto, SubscriptionsModel, type LocalizedString } from '@onrunning/backstage-models';
import { useLocalStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { anySerializer } from '../composables/any-serializer';
import { useLocale } from '../composables/locale';
import { globalConstants } from '../constants/global.constants';
import { UserRole } from '../constants/user-role.constants';
// import { BackstageTheme } from '../models/backstage-theme.enum';
import type { UserProfile } from '../models/login.model';
import type { SFContactModel } from '../models/sf-contact.model';
import { RouteNames } from '../router';
import { LoginService } from '../services/login.service';
import { UsersService } from '../services/users.service';

export const useUserStore = defineStore('user', () => {
  const router = useRouter();
  const { setI18nLocale } = useLocale();
  const { getStartUrl } = useRouterHelpers();
  const { logoutUserBPLog } = useBPLogger();

  const usersService = new UsersService();

  // state

  // in order for useLocalStorage to correctly serialize / deserilize a value when using null as a default value, we need to set the serializer to anySerializer
  // on a note, using undefined instead of null will result in unexpected errors
  const userProfile = useLocalStorage<UserProfile>('user/profile', null, {
    serializer: anySerializer,
  });

  const hasManuallySelectedAnAccount = useLocalStorage<boolean>('user/hasManuallySelectedAnAccount', false);

  const hasClosedReleasesModal = useLocalStorage<boolean>('user/hideNewReleaseModel', false);

  // getters
  // even though userProfile is not typed as nullable, for simplicity reasons, userProfile can be null
  const isAuthenticated = computed(() => !!userProfile.value?.profile);

  const selectedAccount = computed<AccountModel>(() => {
    return userProfile.value?.accounts.find(({ isSelected }) => isSelected) ?? userProfile.value?.accounts[0];
  });

  const sfAccounts = computed<AccountModel[]>(() => {
    return userProfile.value?.accounts;
  });

  const draftInitInfo = computed<DraftInitInfoDto[]>(() => {
    return userProfile.value?.draftInitInfo;
  });

  const selectedAccountId = computed<string>(() => {
    return selectedAccount.value?.id;
  });

  const userLocale = computed<string>(() => {
    // During SF login, the user locale will contain _ instead of - as expected by Intl.DateTimeFormat
    return userProfile?.value?.user?.locale?.replace('_', '-') ?? globalConstants.fallBackLocale;
  });

  // const isDark = computed((): boolean => {
  //   return userProfile.value?.user?.theme === BackstageTheme.dark;
  // });

  const isADealer = computed((): boolean => {
    return userProfile.value?.user?.roles?.includes(UserRole.Dealer);
  });

  const isASalesRep = computed((): boolean => {
    return userProfile.value?.user?.roles?.includes(UserRole.SalesRep);
  });

  function accountIsDistributor(): boolean {
    return selectedAccount.value.isDistributor === true || selectedAccount.value.isDistributor === 'B2BDistrib';
  }

  // actions
  function selectAccount(accountId: string) {
    if (userProfile.value?.accounts) {
      userProfile.value.accounts = userProfile.value?.accounts?.map((account) => {
        account.isSelected = account.id === accountId;

        return account;
      });
    }
  }

  function selectedAccountManually() {
    hasManuallySelectedAnAccount.value = true;
  }

  function setUserProfile(profile: UserProfile) {
    if (profile.accounts[0]) {
      profile.accounts[0].isSelected = true;
    }

    // Users with null locale will get error in the calendar due to missing locale
    // Todo: Move this logic to API
    if (!profile.user.locale) {
      profile.user = {
        id: profile.user.id,
        username: profile.user.username,
        theme: profile.user.theme,
        sfContactId: profile.user.sfContactId,
        email: profile.user.email,
        roles: profile.user.roles,
        lastSeenFeatureReleaseId: profile.user.lastSeenFeatureReleaseId,
        latestFeatureRelease: profile.user.latestFeatureRelease,
      } as any;
    }

    userProfile.value = profile;
  }

  function setClosedReleasesModal(closed: boolean) {
    hasClosedReleasesModal.value = closed;
  }

  async function setUserLocale(locale: keyof LocalizedString) {
    if (userProfile.value) {
      await setI18nLocale(locale);
      userProfile.value.user.locale = locale;

      usersService.updatePreferences({
        locale: userProfile.value.user.locale,
      });
    }
  }

  // async function setUserTheme(theme: BackstageTheme) {
  //   if (userProfile.value) {
  //     userProfile.value.user.theme = theme;

  //     usersService.updatePreferences({
  //       theme: userProfile.value.user.theme,
  //     });
  //   }
  // }

  async function setUserNotificationSubscription(notificationSubscriptions: SubscriptionsModel) {
    if (!userProfile.value) {
      return;
    }

    userProfile.value.user.notificationSubscriptions = notificationSubscriptions;

    if (userProfile.value.profile) {
      userProfile.value.profile.notificationSubscriptions = notificationSubscriptions;
    }

    await usersService.updatePreferences({
      subscriptions: notificationSubscriptions,
    });
  }

  /**
   * @deprecated `isASalesRep` computed property instead
   * This function is deprecated and will be removed in the future.
   */
  function isSalesRep(): boolean {
    const roles = userProfile.value?.user.roles ?? [];

    return roles.includes(UserRole.SalesRep);
  }

  /**
   * @deprecated
   * This function is deprecated and will be removed in the future.
   * Use `isADealer` computed property instead.
   */
  function isDealer(): boolean {
    const roles = userProfile.value?.user.roles ?? [];

    return roles.includes(UserRole.Dealer);
  }

  function isAdmin(): boolean {
    const roles = userProfile.value?.user.roles ?? [];

    return roles.includes(UserRole.Admin);
  }

  function hasBuyerFunction(contact: SFContactModel): boolean {
    return contact.userFunction ? contact.userFunction.includes('Buyer') : false;
  }

  async function logout() {
    await new LoginService().logout();

    if (router.currentRoute.value.path !== '/login') {
      const startUrl = getStartUrl();

      router.replace({
        name: RouteNames.Login,
        ...(startUrl && { query: { startUrl: getStartUrl() } }),
      });
    }

    console.log(logoutUserBPLog(true));
  }

  function $reset() {
    userProfile.value = null;
    hasManuallySelectedAnAccount.value = false;
    hasClosedReleasesModal.value = false;
  }

  return {
    // isDark,
    isAuthenticated,
    userProfile,
    userLocale,
    selectedAccountId,
    hasManuallySelectedAnAccount,
    hasClosedReleasesModal,
    selectedAccount,
    sfAccounts,
    draftInitInfo,
    hasBuyerFunction,
    selectAccount,
    setUserProfile,
    selectedAccountManually,
    setClosedReleasesModal,
    setUserLocale,
    // setUserTheme,
    isSalesRep,
    isASalesRep,
    isAdmin,
    isDealer,
    isADealer,
    logout,
    accountIsDistributor,
    $reset,
    setUserNotificationSubscription,
  };
});
